export type LeadPayload = {
  formType: 'contact' | 'booking' | 'quick-project';
  name: string;
  company?: string;
  email: string;
  phone?: string;
  serviceType?: string;
  budgetRange?: string;
  timeline?: string;
  description: string;
  website?: string;
  startedAt: number;
};

export type ValidationResult = {
  ok: boolean;
  errors: Record<string, string>;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type MaytoMailError = {
  error?: string;
  errors?: Record<string, string>;
};

export function validateLead(payload: LeadPayload): ValidationResult {
  const errors: Record<string, string> = {};

  if (payload.website) {
    errors.website = 'Spam prevention triggered.';
  }

  if (Date.now() - payload.startedAt < 1800) {
    errors.form = 'Please take a moment to complete the form before sending.';
  }

  if (!payload.name.trim()) errors.name = 'Name is required.';
  if (!payload.email.trim()) errors.email = 'Email is required.';
  if (payload.email && !emailPattern.test(payload.email)) errors.email = 'Enter a valid email address.';
  if (!payload.description.trim() || payload.description.trim().length < 15) {
    errors.description = 'Share a few details so we can route your request.';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

export async function sendLead(payload: LeadPayload) {
  const validation = validateLead(payload);
  if (!validation.ok) {
    const error = new Error('Please review the highlighted fields.');
    Object.assign(error, { validation });
    throw error;
  }

  const idempotencyKey = crypto.randomUUID();
  const response = await fetch('/api/leads', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey
    },
    body: JSON.stringify({
      ...payload,
      source: window.location.href,
      idempotencyKey
    })
  });

  if (!response.ok) {
    throw new Error(await getMaytoMailErrorMessage(response));
  }

  return { idempotencyKey };
}

async function getMaytoMailErrorMessage(response: Response) {
  const fallback =
    response.status === 404
      ? 'The email endpoint is not running. Start the site with npm run dev so /api/leads is available.'
      : 'The message could not be sent. Please try again or email MaytoMation directly.';

  try {
    const raw = await response.text();
    if (!raw) return fallback;

    const parsed = JSON.parse(raw) as MaytoMailError;
    if (parsed.errors) {
      const error = new Error(parsed.error ?? 'Please review the highlighted fields.');
      Object.assign(error, { validation: { ok: false, errors: parsed.errors } });
      throw error;
    }
    if (parsed.error) {
      return parsed.error;
    }

    return fallback;
  } catch (error) {
    if (error instanceof Error && 'validation' in error) throw error;
    return fallback;
  }
}
